import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import ReactDatePicker from "react-datepicker";
import { createColumnHelper } from "@tanstack/react-table";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useLabels from "../../hooks/useLabels";
import { getAuditLog } from "../../services/utils";
import Table from "../Table";
import { Icon } from "../Icon";
import "./styles/index.scss";

const ACTION_OPTIONS = ["CREATE", "UPDATE", "DELETE", "COMPLETE", "ROLLBACK", "RECTIFY"];

// Fields that change on every save and carry no user-meaningful info
const SYSTEM_FIELDS = ["updated_at", "created_at", "update_date", "creation_date"];

const formatScalar = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sì" : "no";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const computeDiff = (row) => {
  const { action, old_value: oldVal, new_value: newVal } = row;

  if (action === "UPDATE" && oldVal && newVal && typeof oldVal === "object" && typeof newVal === "object") {
    return Object.keys(newVal)
      .filter((k) => !SYSTEM_FIELDS.includes(k) && JSON.stringify(oldVal[k]) !== JSON.stringify(newVal[k]))
      .map((k) => ({ field: k, before: oldVal[k], after: newVal[k] }));
  }

  if (action === "CREATE" && newVal && typeof newVal === "object") {
    const keys = Object.keys(newVal).filter((k) => !SYSTEM_FIELDS.includes(k) && newVal[k] !== null && newVal[k] !== "");
    return keys.slice(0, 5).map((k) => ({ field: k, after: newVal[k] }));
  }

  if (action === "DELETE" && oldVal && typeof oldVal === "object") {
    return [{ field: "name", before: oldVal.name ?? oldVal.id }];
  }

  if (row.field_name) {
    return [{ field: row.field_name, before: oldVal, after: newVal }];
  }

  return [];
};

/* ─── Action badge ──────────────────────────────────────────── */
const ActionBadge = ({ action, getLabel }) => (
  <span className={`audit_action_badge ${action}`}>
    {getLabel(`auditLogAction_${action}`) || action}
  </span>
);

/* ─── Diff cell ─────────────────────────────────────────────── */
const DiffCell = ({ row }) => {
  const diffs = computeDiff(row.original);
  if (!diffs || diffs.length === 0) return <span className="audit_diff_empty">—</span>;

  return (
    <ul className="audit_diff_list">
      {diffs.map((d, i) => (
        <li key={i} className="audit_diff_item">
          <span className="audit_diff_field">{d.field}</span>
          {d.before !== undefined && (
            <span className="audit_diff_before">{formatScalar(d.before)}</span>
          )}
          {d.before !== undefined && <span className="audit_diff_arrow">→</span>}
          {d.after !== undefined && (
            <span className="audit_diff_after">{formatScalar(d.after)}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

/* ─── Columns definition ────────────────────────────────────── */
const buildColumns = (getLabel) => {
  const h = createColumnHelper();
  return [
    h.accessor((row) => format(new Date(row.performed_at), "dd/MM/yy HH:mm"), {
      id: "performed_at",
      header: getLabel("auditLogColDate"),
      cell: (props) => <span style={{ whiteSpace: "nowrap" }}>{props.getValue()}</span>,
      enableColumnFilter: false,
    }),
    h.accessor("action", {
      header: getLabel("auditLogColAction"),
      cell: (props) => <ActionBadge action={props.getValue()} getLabel={getLabel} />,
      enableColumnFilter: false,
    }),
    h.display({
      id: "diff",
      header: getLabel("auditLogColChanges"),
      cell: (props) => <DiffCell row={props.row} />,
      enableColumnFilter: false,
    }),
    h.accessor("performer_username", {
      header: getLabel("auditLogColUser"),
      cell: (props) => {
        const impersonatedByUsername = props.row.original.impersonated_by_username;
        return (
          <span>
            {props.getValue() || "—"}
            {impersonatedByUsername && (
              <span className="audit_impersonation_badge">
                {" "}({getLabel("auditLogViaImpersonation")} {impersonatedByUsername})
              </span>
            )}
          </span>
        );
      },
      meta: { filterVariant: "text" },
    }),
  ];
};

/* ─────────────────────────────────────────────────────────────────
   AuditLogWidget — pannello collassabile da inserire nei detail
───────────────────────────────────────────────────────────────── */
const AuditLogWidget = ({ entityType, entityId }) => {
  const [getLabel] = useLabels();
  const axiosPrivate = useAxiosPrivate();

  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const LIMIT = 10;

  const fetchEntries = useCallback(async () => {
    if (!open || !entityType || !entityId) return;
    setLoading(true);
    const result = await getAuditLog(axiosPrivate, {
      entity_type: entityType,
      entity_id: entityId,
      page,
      limit: LIMIT,
      ...(actionFilter && { action: actionFilter }),
      ...(fromDate && { from_date: fromDate.toISOString() }),
      ...(toDate && { to_date: toDate.toISOString() }),
    });
    setEntries(result.data || []);
    setTotal(result.total || 0);
    setLoading(false);
  }, [open, entityType, entityId, page, actionFilter, fromDate, toDate, axiosPrivate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { setPage(0); }, [actionFilter, fromDate, toDate]);

  const columns = buildColumns(getLabel);
  const seeAllUrl = `/audit?entity_type=${entityType}&entity_id=${entityId}`;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="audit_widget">
      <button
        className={`audit_widget_toggle${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon icon="fa-clock-rotate-left" />
        {getLabel("auditLogToggleShow")}
        <Icon
          icon="fa-angle-down"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s" }}
        />
      </button>

      {open && (
        <div className="audit_widget_body">
          <div className="audit_filters">
            <label>
              {getLabel("auditLogFilterAction")}
              <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
                <option value="">—</option>
                {ACTION_OPTIONS.map((a) => (
                  <option key={a} value={a}>{getLabel(`auditLogAction_${a}`) || a}</option>
                ))}
              </select>
            </label>

            <label>
              {getLabel("auditLogFilterFrom")}
              <ReactDatePicker
                selected={fromDate}
                onChange={setFromDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="gg/mm/aaaa"
                isClearable
              />
            </label>
            <label>
              {getLabel("auditLogFilterTo")}
              <ReactDatePicker
                selected={toDate}
                onChange={setToDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="gg/mm/aaaa"
                isClearable
              />
            </label>
          </div>

          {loading ? (
            <p className="audit_loading">{getLabel("auditLogLoading")}</p>
          ) : entries.length === 0 ? (
            <p className="audit_empty">{getLabel("auditLogEmpty")}</p>
          ) : (
            <Table
              defaultData={entries}
              columns={columns}
              isEditable={false}
              isSearchBar={false}
              isPagination={false}
              isSorting={false}
              isLegend={false}
            />
          )}

          {totalPages > 1 && (
            <div className="audit_pagination">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span>{page + 1} / {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          )}

          {total > 0 && (
            <Link to={seeAllUrl} className="audit_see_all">
              {getLabel("auditLogSeeAll")} ({total}) →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export { ActionBadge, buildColumns };
export default AuditLogWidget;
